import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Chip,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Checkbox,
  Skeleton,
} from '@mui/material';
import {
  Close,
  Payment as PaymentIcon,
  AccountBalance,
  CheckCircle,
  TrendingDown,
  Receipt,
} from '@mui/icons-material';
import { addPayment, subscribeToPayments, cleanupCreditorBills } from '../firebase/creditorService';
import { getBills } from '../firebase/billingService';
import { creditCashAccount } from '../firebase/cashBankService';

const fmt = (n) =>
  (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RECEIVED_VIA_OPTIONS = [
  { value: 'cash_in_hand', label: 'Cash in Hand', method: 'cash' },
  { value: 'cash_at_bank', label: 'Bank Transfer', method: 'bank' },
  { value: 'petty_cash', label: 'Petty Cash', method: 'cash' },
  { value: 'cash_at_bank_cheque', label: 'Cheque (Cash at Bank)', method: 'cheque' },
];

// ─────────────────────────────────────────────────────────────
// VIEW DIALOG — customer info, bills, payment history
// ─────────────────────────────────────────────────────────────
export const CreditorViewDialog = ({ open, creditor, onClose, onPayClick }) => {
  const [payments, setPayments] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!creditor) return;
    setLoading(true);

    const unsubscribe = subscribeToPayments(creditor.id, (data) => {
      setPayments(data);
    });

    const billsResult = await getBills();
    if (billsResult.success) {
      const allBillIds = creditor.bills || [];
      const linked = billsResult.data.filter(b => allBillIds.includes(b.id));
      const existingIds = linked.map(b => b.id);
      const orphaned = allBillIds.filter(id => !existingIds.includes(id));
      if (orphaned.length > 0) {
        await cleanupCreditorBills(creditor.id, existingIds);
      }
      setBills(linked);
    }

    setLoading(false);
    return () => unsubscribe();
  }, [creditor]);

  useEffect(() => {
    if (open && creditor) loadData();
  }, [open, creditor, loadData]);

  if (!creditor) return null;

  const unpaidBills = bills.filter(b => b.status !== 'Paid');
  const settledBills = creditor.settledBills || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AccountBalance color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>{creditor.customerName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {creditor.customerPhone && `${creditor.customerPhone}  ·  `}
                {creditor.customerEmail || ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {creditor.status !== 'Settled' && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PaymentIcon />}
                onClick={onPayClick}
              >
                Record Payment
              </Button>
            )}
            <IconButton onClick={onClose} size="small"><Close /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 2 }}>
            <Skeleton variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
          </Box>
        ) : (
          <Box>
            {/* Balance summary strip */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary">Total Credited</Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  LKR {fmt(creditor.totalCredited)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  LKR {fmt(creditor.totalPaid)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary">Outstanding Balance</Typography>
                <Typography variant="h6" fontWeight={700} color={creditor.balance > 0 ? 'error.main' : 'success.main'}>
                  LKR {fmt(creditor.balance)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1, minWidth: 100, display: 'flex', alignItems: 'center' }}>
                <Chip
                  label={creditor.status}
                  color={creditor.status === 'Settled' ? 'success' : creditor.status === 'Overdue' ? 'error' : 'warning'}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </Box>

            {/* Bills */}
            <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Receipt fontSize="small" /> Invoices ({unpaidBills.length + settledBills.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Invoice #</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="right"><strong>Amount (LKR)</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unpaidBills.map(bill => (
                    <TableRow key={bill.id} hover>
                      <TableCell>
                        <Typography variant="body2" color="primary" fontWeight={600}>
                          {bill.invoiceNumber || bill.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{bill.date}</TableCell>
                      <TableCell align="right">{fmt(bill.total)}</TableCell>
                      <TableCell>
                        <Chip label={bill.paymentType || 'Credit'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={bill.status}
                          size="small"
                          color={bill.status === 'Paid' ? 'success' : (bill.status === 'Koko' || bill.status === 'MintPay') ? 'warning' : 'warning'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {settledBills.map((sb, i) => (
                    <TableRow key={`s-${i}`} sx={{ opacity: 0.7 }}>
                      <TableCell>
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {sb.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{sb.date}</TableCell>
                      <TableCell align="right">{fmt(sb.amount)}</TableCell>
                      <TableCell>
                        <Chip label={sb.paymentType || 'Credit'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label="Paid" size="small" color="success" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {unpaidBills.length === 0 && settledBills.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">No linked invoices</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Payment history */}
            <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle fontSize="small" color="success" /> Payment History ({payments.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="right"><strong>Amount (LKR)</strong></TableCell>
                    <TableCell><strong>Invoice(s)</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...payments].sort((a, b) => new Date(b.date) - new Date(a.date)).map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.date}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {fmt(p.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(p.invoiceNumbers || []).length > 0 ? (
                          <Chip label={(p.invoiceNumbers || []).join(', ')} size="small" color="primary" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">General payment</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{p.notes || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {payments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">No payments recorded yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
// PAYMENT DIALOG — focused, fast, no scrolling
// ─────────────────────────────────────────────────────────────
export const CreditorPaymentDialog = ({ open, creditor, onClose, onSuccess, currentUser }) => {
  const [bills, setBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(true);
  const [selectedBills, setSelectedBills] = useState([]);
  const [amount, setAmount] = useState('');
  const [receivedVia, setReceivedVia] = useState('cash_in_hand');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load unpaid bills when dialog opens
  useEffect(() => {
    if (!open || !creditor) return;
    setLoadingBills(true);
    setSelectedBills([]);
    setAmount(creditor.balance > 0 ? creditor.balance.toFixed(2) : '');
    setReceivedVia('cash_in_hand');
    setDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setError('');

    getBills().then(result => {
      if (result.success) {
        const allBillIds = creditor.bills || [];
        const linked = result.data.filter(b => allBillIds.includes(b.id) && b.status !== 'Paid');
        setBills(linked);
      }
      setLoadingBills(false);
    });
  }, [open, creditor]);

  const toggleBill = (bill) => {
    const isSelected = selectedBills.some(b => b.id === bill.id);
    const next = isSelected
      ? selectedBills.filter(b => b.id !== bill.id)
      : [...selectedBills, bill];
    setSelectedBills(next);
    // Auto-fill amount from selected bills
    const total = next.reduce((s, b) => s + (b.total || 0), 0);
    setAmount(next.length > 0 ? total.toFixed(2) : creditor.balance.toFixed(2));
    setError('');
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Enter a valid amount'); return; }
    if (amt > creditor.balance) { setError(`Cannot exceed outstanding balance (LKR ${fmt(creditor.balance)})`); return; }

    if (selectedBills.length > 0) {
      const selectedTotal = selectedBills.reduce((s, b) => s + (b.total || 0), 0);
      if (Math.abs(amt - selectedTotal) > 0.01) {
        setError(`Amount must match selected invoices total (LKR ${fmt(selectedTotal)})`);
        return;
      }
    }

    setSubmitting(true);
    setError('');

    const paymentData = {
      amount: amt,
      date,
      notes,
      selectedBills: selectedBills.map(b => b.id),
    };

    const result = await addPayment(creditor.id, paymentData);

    if (result.success) {
      const opt = RECEIVED_VIA_OPTIONS.find(o => o.value === receivedVia) || RECEIVED_VIA_OPTIONS[0];
      creditCashAccount(
        opt.value.replace('_cheque', ''),
        amt,
        creditor.id,
        'creditor_payment',
        `Payment - ${creditor.customerName}`,
        opt.method,
        null,
        currentUser
      ).catch(err => console.error('Cash ledger credit failed:', err));

      onSuccess(result.message || 'Payment recorded successfully');
      onClose();
    } else {
      setError(result.error || 'Failed to record payment');
    }

    setSubmitting(false);
  };

  if (!creditor) return null;

  const unpaidBills = bills;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PaymentIcon color="success" />
            <Box>
              <Typography variant="h6" fontWeight={700}>Record Payment</Typography>
              <Typography variant="caption" color="text.secondary">{creditor.customerName}</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Balance strip */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            mb: 2.5,
            borderRadius: 2,
            backgroundColor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingDown color="error" fontSize="small" />
            <Typography variant="body2" color="text.secondary">Outstanding</Typography>
          </Box>
          <Typography variant="h6" fontWeight={700} color="error.main">
            LKR {fmt(creditor.balance)}
          </Typography>
        </Box>

        {/* Invoice selection */}
        {loadingBills ? (
          <Skeleton variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 1 }} />
        ) : unpaidBills.length > 0 ? (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Select invoice(s) to settle <Typography component="span" variant="caption" color="text.secondary">(optional — auto-fills amount)</Typography>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {unpaidBills.map(bill => {
                const isSelected = selectedBills.some(b => b.id === bill.id);
                return (
                  <Box
                    key={bill.id}
                    onClick={() => toggleBill(bill)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      border: '1.5px solid',
                      borderColor: isSelected ? 'success.main' : 'divider',
                      backgroundColor: isSelected ? 'rgba(46,125,50,0.06)' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'success.main', backgroundColor: 'rgba(46,125,50,0.04)' },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      color="success"
                      sx={{ p: 0 }}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleBill(bill)}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {bill.invoiceNumber || bill.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {bill.date} · {bill.paymentType || 'Credit'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700}>
                      LKR {fmt(bill.total)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 2.5 }} icon={false}>
            No unpaid invoices linked — this will be recorded as a general payment.
          </Alert>
        )}

        <Divider sx={{ mb: 2.5 }} />

        {/* Payment fields */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={amount}
              onChange={e => { setAmount(e.target.value); setError(''); }}
              disabled={selectedBills.length > 0}
              InputProps={{ startAdornment: <InputAdornment position="start">LKR</InputAdornment> }}
              helperText={selectedBills.length > 0 ? 'Auto-filled from selected invoices' : ' '}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Received Via"
              select
              value={receivedVia}
              onChange={e => setReceivedVia(e.target.value)}
              size="small"
            >
              {RECEIVED_VIA_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes (optional)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Cheque #1234, reference number..."
              size="small"
            />
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined" size="small">Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={submitting || !amount || parseFloat(amount) <= 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
        >
          {submitting ? 'Recording...' : `Record LKR ${parseFloat(amount) > 0 ? fmt(parseFloat(amount)) : '—'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
// DEFAULT EXPORT — backwards-compatible wrapper
// Opens view dialog; payment dialog opens on top when needed
// ─────────────────────────────────────────────────────────────
const CreditorDetail = ({ open, creditor, onClose, onUpdate, viewOnly = false, currentUser = null }) => {
  const [showPayment, setShowPayment] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // If opened directly in payment mode (from payment icon), show payment dialog immediately
  const [mode, setMode] = useState(viewOnly ? 'view' : 'payment');

  useEffect(() => {
    if (open) setMode(viewOnly ? 'view' : 'payment');
  }, [open, viewOnly]);

  const handlePaySuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
    setShowPayment(false);
    onUpdate();
  };

  if (!creditor) return null;

  return (
    <>
      {/* View dialog — always rendered when open in view mode */}
      <CreditorViewDialog
        open={open && mode === 'view'}
        creditor={creditor}
        onClose={onClose}
        onPayClick={() => setShowPayment(true)}
      />

      {/* Payment dialog — opened from view dialog OR directly from table */}
      <CreditorPaymentDialog
        open={(open && mode === 'payment') || showPayment}
        creditor={creditor}
        onClose={() => {
          setShowPayment(false);
          if (mode === 'payment') onClose();
        }}
        onSuccess={handlePaySuccess}
        currentUser={currentUser}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreditorDetail;
